if (!customElements.get('shipping-estimator')) {
  customElements.define(
    'shipping-estimator',
    class ShippingEstimator extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        this.zip = this.querySelector('[name="shipping_address[zip]"]');
        this.province = this.querySelector('[name="shipping_address[province]"]');
        this.button = this.querySelector('button[type="submit"]');
        this.status = this.querySelector('[role="status"]');
        this.rates = this.querySelector('.shipping-estimator__rates');
        this.button.dataset.defaultText = this.button.textContent.trim();
        this.requestNumber = 0;
        this.hasEstimated = false;
        this.form.addEventListener('submit', (event) => this.onSubmit(event));
        this.form.addEventListener('change', () => this.onDestinationChange());

        this.drawerDetails = this.closest('.shipping-estimator-drawer');
        if (this.drawerDetails) {
          this.drawerDetails.open = window.sessionStorage.getItem('shippingEstimatorDrawerOpen') === 'true';
          this.drawerDetails.addEventListener('toggle', () => {
            window.sessionStorage.setItem('shippingEstimatorDrawerOpen', this.drawerDetails.open);
          });
        }

        const savedZip = window.sessionStorage.getItem('shippingEstimatorZip');
        const savedProvince = window.sessionStorage.getItem('shippingEstimatorProvince');
        if (savedZip && savedProvince) {
          this.zip.value = savedZip;
          this.province.value = savedProvince;
          this.hasEstimated = true;
          this.estimate();
        }

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => this.onCartUpdate(event));
        }
      }

      disconnectedCallback() {
        if (this.unsubscribe) this.unsubscribe();
        window.clearTimeout(this.destinationTimer);
        window.clearTimeout(this.cartTimer);
      }

      onSubmit(event) {
        event.preventDefault();
        if (!this.validateDestination()) return;
        this.hasEstimated = true;
        window.sessionStorage.setItem('shippingEstimatorZip', this.zip.value.trim());
        window.sessionStorage.setItem('shippingEstimatorProvince', this.province.value);
        this.estimate();
      }

      onDestinationChange() {
        if (!this.hasEstimated || !this.validateDestination(false)) return;
        window.sessionStorage.setItem('shippingEstimatorZip', this.zip.value.trim());
        window.sessionStorage.setItem('shippingEstimatorProvince', this.province.value);
        window.clearTimeout(this.destinationTimer);
        this.destinationTimer = window.setTimeout(() => this.estimate(), 300);
      }

      onCartUpdate(event) {
        if (!this.hasEstimated) return;
        if (event.cartData && event.cartData.item_count === 0) {
          this.clearResults();
          return;
        }
        window.clearTimeout(this.cartTimer);
        this.cartTimer = window.setTimeout(() => this.estimate(), 300);
      }

      validateDestination(showMessage = true) {
        const validZip = /^\d{5}$/.test(this.zip.value.trim());
        const valid = validZip && this.province.value !== '';
        if (!valid && showMessage) {
          this.showError(this.dataset.invalid);
        }
        return valid;
      }

      async estimate() {
        const requestNumber = ++this.requestNumber;
        const params = this.destinationParams();
        this.setLoading(true);
        this.rates.replaceChildren();

        try {
          await this.request(`${this.rootUrl()}cart/prepare_shipping_rates.json?${params}`, { method: 'POST' });
          const result = await this.pollForRates(params, requestNumber);
          if (requestNumber !== this.requestNumber) return;
          this.renderRates(result.shipping_rates || []);
        } catch (error) {
          if (requestNumber !== this.requestNumber) return;
          this.showError(error.message);
        } finally {
          if (requestNumber === this.requestNumber) this.setLoading(false);
        }
      }

      async pollForRates(params, requestNumber) {
        for (let attempt = 0; attempt < 8; attempt += 1) {
          await this.wait(Math.min(500 + attempt * 250, 2000));
          if (requestNumber !== this.requestNumber) throw new Error('Shipping estimate replaced by a newer request.');
          const response = await this.request(`${this.rootUrl()}cart/async_shipping_rates.json?${params}`);
          if (response) return response;
        }
        throw new Error(this.dataset.slow);
      }

      async request(url, options = {}) {
        const response = await fetch(url, { ...options, headers: { Accept: 'application/json' } });
        const data = await response.json().catch(() => null);
        if (response.ok) return data;
        const message = data?.error || data?.errors || this.dataset.unavailable;
        throw new Error(Array.isArray(message) ? message.join(' ') : this.flattenMessage(message));
      }

      flattenMessage(message) {
        if (typeof message === 'string') return message;
        return Object.values(message).flat().join(' ');
      }

      renderRates(rates) {
        this.rates.replaceChildren();
        if (rates.length === 0) {
          this.showError(this.dataset.noRates);
          return;
        }

        rates.forEach((rate) => this.rates.append(this.rateItem(rate)));
        this.status.textContent = this.dataset.ready;
      }

      rateItem(rate) {
        const item = document.createElement('li');
        const details = document.createElement('div');
        const name = document.createElement('strong');
        const price = document.createElement('span');
        item.className = 'shipping-estimator__rate';
        name.textContent = rate.presentment_name || rate.name;
        price.className = 'shipping-estimator__rate-price';
        price.textContent = this.formatMoney(rate.price, rate.currency || this.dataset.currency);
        details.append(name);

        const delivery = this.deliveryText(rate);
        if (delivery) {
          const estimate = document.createElement('div');
          estimate.className = 'caption-large';
          estimate.textContent = delivery;
          details.append(estimate);
        }

        item.append(details, price);
        return item;
      }

      deliveryText(rate) {
        if (Array.isArray(rate.delivery_range) && rate.delivery_range.length === 2) {
          const start = this.formatDate(rate.delivery_range[0]);
          const end = this.formatDate(rate.delivery_range[1]);
          return start === end
            ? this.dataset.deliveryDate.replace('[date]', start)
            : this.dataset.deliveryRange.replace('[start]', start).replace('[end]', end);
        }
        if (rate.delivery_date) {
          return this.dataset.deliveryDate.replace('[date]', this.formatDate(rate.delivery_date));
        }
        if (Array.isArray(rate.delivery_days) && rate.delivery_days.length === 2) {
          const [start, end] = rate.delivery_days;
          return start === end
            ? this.dataset.deliveryDays.replace('[days]', start)
            : this.dataset.deliveryDaysRange.replace('[start]', start).replace('[end]', end);
        }
        return '';
      }

      formatDate(value) {
        const [year, month, day] = value.split('-').map(Number);
        return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(new Date(year, month - 1, day));
      }

      formatMoney(value, currency) {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(Number(value));
      }

      destinationParams() {
        return new URLSearchParams({
          'shipping_address[zip]': this.zip.value.trim(),
          'shipping_address[country]': 'United States',
          'shipping_address[province]': this.province.value,
        }).toString();
      }

      rootUrl() {
        return this.dataset.rootUrl.endsWith('/') ? this.dataset.rootUrl : `${this.dataset.rootUrl}/`;
      }

      setLoading(loading) {
        this.button.disabled = loading;
        this.button.textContent = loading ? this.dataset.calculatingButton : this.button.dataset.defaultText;
        this.status.textContent = loading ? this.dataset.calculatingStatus : this.status.textContent;
      }

      showError(message) {
        this.rates.replaceChildren();
        this.status.textContent = message;
      }

      clearResults() {
        this.requestNumber += 1;
        this.hasEstimated = false;
        window.sessionStorage.removeItem('shippingEstimatorZip');
        window.sessionStorage.removeItem('shippingEstimatorProvince');
        this.rates.replaceChildren();
        this.status.textContent = '';
      }

      wait(milliseconds) {
        return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
      }
    }
  );
}
